import { Component, computed, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AsideHeaderComponent } from '../shared/aside-header/aside-header.component';

import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Account } from '../../class/Account';
import { Company } from '../../class/Company';
import { AccountStateService } from '../../services/account-state.service';
import { ConnectionService } from '../../services/connection.service';
import { RoutingService } from '../../services/routing.service';
import { normalizeString } from '../../utils/normalize-string';
import { ContainerComponent } from '../layout/container/container.component';
import { LayoutComponent } from '../layout/layout.component';
import { LeftPanelComponent } from '../layout/left-panel/left-panel.component';
import { SubNavigationItemComponent } from '../layout/sub-navigation/sub-navigation-item/sub-navigation-item.component';
import { SubNavigationComponent } from '../layout/sub-navigation/sub-navigation.component';
import { TopBarComponent } from '../layout/top-bar/top-bar.component';
import { CardItemComponent } from '../shared/card-item/card-item.component';
import { AlertComponent } from '../shared/card/alert/alert.component';

@Component({
  selector: 'supervisor',

  imports: [
    LayoutComponent,
    LeftPanelComponent,
    ContainerComponent,
    SubNavigationItemComponent,
    TopBarComponent,
    SubNavigationComponent,
    TranslatePipe,
    CardItemComponent,
    MatTooltipModule,
    AsideHeaderComponent,
    AlertComponent,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    FormsModule,
  ],
  templateUrl: './supervisor.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './supervisor.component.scss',
})
export class SupervisorComponent implements OnInit {
  private _accountStateService = inject(AccountStateService);
  private _connectionService = inject(ConnectionService);
  private _routingService = inject(RoutingService);
  public $isMobile = this._routingService.$isMobile;
  public $accounts = this._accountStateService.$accountsList;
  public $companyList = this._accountStateService.$companyList;
  public $selectedAccount = this._accountStateService.$account;
  public $companyListFiltered = signal<Company[] | null>(null);

  public search = new FormControl('');

  public $companyListFinal = computed(() => {
    return this.$companyListFiltered() !== null ? this.$companyListFiltered() : this.$companyList();
  });

  public isSupervisor = computed(() => {
    const accounts = this.$accounts();
    return accounts?.some(account => account.supervisor) ?? false;
  });

  ngOnInit() {
    this.search.valueChanges.subscribe(search => {
      const companyList = this.$companyList();
      const filtered = companyList.filter(company => {
        const matchesName = normalizeString(company.full_name).indexOf(normalizeString(search)) !== -1;

        const matchesUid = company.uid === Number(search);

        return matchesName || matchesUid;
      });
      this.$companyListFiltered.set(search?.length > 2 ? filtered : null);
    });
  }

  public selectAccount(account: Account) {
    this._connectionService.validateAccount(account);
  }

  public validateCompany(company: Company) {
    this._connectionService.validateCompany(company);
  }

  clearSearch() {
    this.search.setValue('');
    this.$companyListFiltered.set(null);
  }

  public clearAccount() {
    this.clearSearch();
    this._accountStateService.setAccount(null);
    this._accountStateService.setCompanyList(null);
  }
}
